import { FunctionComponent, useState, useEffect, useContext, useMemo, useCallback } from "react";
import { 
  Typography, 
  Button, 
  CircularProgress
} from "@mui/material";
import FilterListIcon from '@mui/icons-material/FilterList';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import styles from "./NewTickets.module.css";
import PaginatedTable from "../../components/shared-components/table/PaginatedTable";
import NewListTickets from "./NewListTickets";
import NewSoldTickets from "./NewSoldTickets";
import NewDelistedTickets from "./NewDelistedTickets";
import NewUnsoldTickets from "./NewUnsoldTickets";
import { useLazyQuery } from "@apollo/client";
import { GET_NEW_TICKETS, GET_LEAGUES_LIST, GET_NEW_TICKETS_GROUPED } from "./api/TicketsQuery";
import { NewListTicketsResponse, NewListTicket } from "./NewTicketsTypes";
import moment from "moment";
import { globalContext } from "../../Provider/AppProvider";
import TicketFilterDropdown from "../../components/shared-components/dropdown/filter-dropdown/TicketFilterDropdown";
import DateRangeFilter from "../../components/shared-components/dropdown/filter-dropdown/DateRangeFilter";
import { extractLeagues, formatDateWithHyphen } from "../../helper";
import {
  sortNewTicketsPage,
  type NewTicketsSortDirection,
} from "./newTicketsSort";

const LISTED_STATUS_OPTIONS = [
    { id: 'List', label: 'Listed' },
    { id: 'DelistInProgress', label: 'Delist Requested' }
];

const SOLD_STATUS_OPTIONS = [
    { id: 'NotInitiated', label: 'Sold' },
    { id: 'Inprogress', label: 'In Progress' },
    { id: 'Success', label: 'Settled' },
    { id: 'Failed', label: 'Failed' },
    { id: 'Voided_Payout', label: 'Voided Payout' }
];

const NewTickets: FunctionComponent = () => {
  const [activeTab, setActiveTab] = useState(0);
  const {globalState,handleGlobalState} = useContext<any>(globalContext);

  const tabs = ["Listed", "Sold", "Delisted", "Unsold"];
  const currentStatus = tabs[activeTab];

  const filterKey = activeTab === 0 ? 'newListTicketsFilter' : 
                    activeTab === 1 ? 'newSoldTicketsFilter' :
                    activeTab === 2 ? 'newDelistedTicketsFilter' :
                    'newUnsoldTicketsFilter';

  const currentFilter = globalState?.[filterKey];

  // Pagination
  const [page, setPage] = useState(1);
  const rowsPerPage = 10;
  
  const [tickets, setTickets] = useState<NewListTicket[]>([]);
  const [totalPages, setTotalPages] = useState(0);
  const [searchvalue, setSearchvalue] = useState(globalState?.globalSearch?.searchtext);
  const [sortColumn, setSortColumn] = useState<string | null>(null);
  const [sortDirection, setSortDirection] =
    useState<NewTicketsSortDirection>("asc");

  const sortedTickets = useMemo(
    () => sortNewTicketsPage(tickets, sortColumn, sortDirection),
    [tickets, sortColumn, sortDirection]
  );

  const handleRequestSort = useCallback((columnId: string) => {
    setSortColumn((prev) => {
      if (prev === columnId) {
        setSortDirection((d) => (d === "asc" ? "desc" : "asc"));
        return prev;
      }
      setSortDirection("asc");
      return columnId;
    });
  }, []);

  useEffect(() => {
    setSortColumn(null);
    setSortDirection("asc");
  }, [activeTab]);

  const [leaguesOptions, setLeaguesOptions] = useState<{id: string, label: string}[]>([]);

  const [getLeaguesList] = useLazyQuery(GET_LEAGUES_LIST, {
    fetchPolicy: 'no-cache',
    onCompleted: (data) => {
      if (data?.leagues) {
        const filtered = data.leagues
            .filter((item: any) => !extractLeagues.includes(item.l_name))
            .map((item: any) => ({
                id: item.l_id,
                label: item.l_name
            }));
        setLeaguesOptions(filtered);
      }
    }
  });

  useEffect(() => {
    getLeaguesList();
  }, [getLeaguesList]);

  const [getNewListTickets, { loading, error }] = useLazyQuery<NewListTicketsResponse>(GET_NEW_TICKETS_GROUPED, {
    fetchPolicy: "network-only",
    notifyOnNetworkStatusChange: true,
    onCompleted: (data) => {
        const fetchedTickets =
          (data as any)?.getTicketsGrouped?.filterlistticketswithvlids || [];
        setTickets(fetchedTickets);
        const totalCount =
          fetchedTickets.length > 0 ? fetchedTickets[0].total_count : 0;
        setTotalPages(Math.ceil(totalCount / rowsPerPage));
    },
    onError: (error) => {
        console.error("Error fetching tickets:", error);
    }
  });

  useEffect(() => {
    console.log("activeTab", activeTab, currentFilter?.ticketStatus);
    console.log("value is", currentFilter?.ticketStatus?.length > 0 
                      ? ((currentFilter?.ticketStatus === "Voided_Payout") ? null : currentFilter?.ticketStatus) 
                      : null);
    if (activeTab === 0 || activeTab === 1 || activeTab === 2 || activeTab === 3) {
      if(globalState?.ticketUpdate?.delistTicket === true || globalState?.ticketUpdate?.listPrice === true || currentFilter?.isFilter || !currentFilter?.isFilter){
        console.log("globalState", globalState);
        const ticketsVariables= {
        leagueId:  globalState?.notification?.notificationClicked ? null: globalState?.error?.errorClicked? null : (currentFilter?.leagueId?.length > 0 ? currentFilter?.leagueId : null),
        ticketStatus:  globalState?.notification?.notificationClicked ? null: globalState?.error?.errorClicked ? null : 
          (currentFilter?.ticketStatus?.length > 0 
            ? (activeTab === 1 ? ["Sold"] : currentFilter?.ticketStatus) 
            : (activeTab === 0 ? ["List", "DelistInProgress"] : activeTab === 1 ? ["Sold"] : activeTab === 2 ? ["Return"] : ["Unsold"])),
        startdate:  globalState?.notification?.notificationClicked ? null:globalState?.error?.errorClicked? null : currentFilter?.startdate ? currentFilter?.startdate : currentFilter?.startDateRange,
        enddate:  globalState?.notification?.notificationClicked ? null:globalState?.error?.errorClicked? null : currentFilter?.enddate ? currentFilter?.enddate : currentFilter?.endDateRange,
        pageSize: rowsPerPage,
        pageOffset: (page -1)*rowsPerPage,
        ticketId: globalState.notification.ticketId,
        ticketPlacementId: globalState.notification.tpId,
        paymentStatus: ((activeTab === 1) && currentFilter?.ticketStatus?.length > 0) 
                      ? currentFilter?.ticketStatus
                      : null,
        payoutType: ((activeTab === 1) && currentFilter?.ticketStatus?.length > 0)
                      ? ((currentFilter?.ticketStatus?.includes("Voided_Payout")) ? "Voided_Payout" : 'Unvoided_Payout') 
                      : null,
        array_tpid: globalState?.notification.tp_id_array ? globalState?.notification.tp_id_array : globalState?.error?.approximateTpArray?.length > 0  ? globalState.error.approximateTpArray : globalState.error.ticketPlacementId
        };

        getNewListTickets({
          variables: ticketsVariables
        });
        
        const tempState = {...globalState};
        tempState.ticketUpdate.delistTicket = false;
        tempState.ticketUpdate.listPrice = false;
        handleGlobalState(tempState);
      }
    }
  }, [page,
     activeTab,
     rowsPerPage,
     getNewListTickets,
     globalState?.ticketUpdate?.delistTicket,
     globalState?.ticketUpdate?.listPrice,
     currentFilter?.leagueId,
     currentFilter?.ticketStatus
  ]);


  useEffect(() => {
    const filterStartDate = currentFilter?.startdate;
    const filterEndDate = currentFilter?.enddate;

    console.log("filtered date", filterStartDate, filterEndDate);
    if (activeTab === 0 || activeTab === 1 || activeTab === 2 || activeTab === 3) {
      if((filterStartDate && filterEndDate) || (!filterStartDate && !filterEndDate)) {
        console.log("globalState", globalState);
        const ticketsVariables= {
        leagueId:  globalState?.notification?.notificationClicked ? null: globalState?.error?.errorClicked? null : (currentFilter?.leagueId?.length > 0 ? currentFilter?.leagueId : null),
        ticketStatus:  globalState?.notification?.notificationClicked ? null: globalState?.error?.errorClicked ? null : 
          (currentFilter?.ticketStatus?.length > 0 
            ? (activeTab === 1 ? ["Sold"] : currentFilter?.ticketStatus) 
            : (activeTab === 0 ? ["List", "DelistInProgress"] : activeTab === 1 ? ["Sold"] : activeTab === 2 ? ["Return"] : ["Unsold"])),
        startdate:  globalState?.notification?.notificationClicked ? null:globalState?.error?.errorClicked? null : currentFilter?.startdate ? currentFilter?.startdate : currentFilter?.startDateRange,
        enddate:  globalState?.notification?.notificationClicked ? null:globalState?.error?.errorClicked? null : currentFilter?.enddate ? currentFilter?.enddate : currentFilter?.endDateRange,
        pageSize: rowsPerPage,
        pageOffset: (page -1)*rowsPerPage,
        ticketId: globalState.notification.ticketId,
        ticketPlacementId: globalState.notification.tpId,
        paymentStatus: ((activeTab === 1) && currentFilter?.ticketStatus?.length > 0) 
                      ? currentFilter?.ticketStatus
                      : null,
        payoutType: ((activeTab === 1) && currentFilter?.ticketStatus?.length > 0)
                      ? ((currentFilter?.ticketStatus?.includes("Voided_Payout")) ? "Voided_Payout" : 'Unvoided_Payout') 
                      : null,
        array_tpid: globalState?.notification.tp_id_array ? globalState?.notification.tp_id_array : globalState?.error?.approximateTpArray?.length > 0  ? globalState.error.approximateTpArray : globalState.error.ticketPlacementId
        };

        getNewListTickets({
          variables: ticketsVariables
        });
      }
    }
  }, [
     getNewListTickets,
     currentFilter?.startdate,
     currentFilter?.enddate,
     currentFilter?.startDateRange,
     currentFilter?.endDateRange,
  ]);

  useEffect(() => {
    const tempdata = {...globalState}
    setSearchvalue(tempdata?.globalSearch?.searchtext)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [globalState?.globalSearch?.searchtext]);  


  return (
    <div className={styles.container}>
      <div className={styles.topBar}>
        <div className={styles.breadcrumb}>
            <Typography variant="caption" className={styles.subHeader}>Manage Tickets</Typography>
            <Typography variant="h5" className={styles.pageTitle}>{currentStatus} Tickets</Typography>
        </div>
        <div className={styles.controls}>
             {(activeTab !== 2 && activeTab !== 3) && (
               <TicketFilterDropdown 
                  label="Status"
                  options={activeTab === 0 ? LISTED_STATUS_OPTIONS : SOLD_STATUS_OPTIONS}
                  styleValues = {{
                    Height: 'auto',
                  }}
                  selectedValue={currentFilter?.ticketStatus}
                  onSelect={(id) => {
                      const temp = { ...globalState };
                      let current = temp[filterKey].ticketStatus;
                      if (!Array.isArray(current)) {
                          current = current ? [current] : [];
                      }
                      if (current.includes(id)) {
                          temp[filterKey].ticketStatus = current.filter((x: string) => x !== id);
                      } else {
                          let nextStatus = [...current, id];
                          if (activeTab === 1) {
                              if (id === 'Voided_Payout') {
                                  // Remove Inprogress, Success, Failed
                                  nextStatus = nextStatus.filter(x => !['Inprogress', 'Success', 'Failed'].includes(x));
                              } else if (['Inprogress', 'Success', 'Failed'].includes(id)) {
                                  // Remove Voided_Payout
                                  nextStatus = nextStatus.filter(x => x !== 'Voided_Payout');
                              }
                          }
                          temp[filterKey].ticketStatus = nextStatus;
                      }
                      temp[filterKey].isFilter = true;
                      handleGlobalState(temp);
                  }}
                  onClear={() => {
                      if(currentFilter?.ticketStatus?.length > 0){
                          const temp = { ...globalState };
                          temp[filterKey].ticketStatus = [];
                          handleGlobalState(temp);
                      }
                  }}
                  icon={<FilterListIcon />}
               />
             )}
             <TicketFilterDropdown 
                label="Event Types"
                options={leaguesOptions}
                styleValues = {{
                  Height: '300px',
                }}
                selectedValue={currentFilter?.leagueId}
                onSelect={(id) => {
                    const temp = { ...globalState };
                    let current = temp[filterKey].leagueId;
                    if (!Array.isArray(current)) {
                        current = current ? [current] : [];
                    }
                    if (current.includes(id)) {
                        temp[filterKey].leagueId = current.filter((x: string) => x !== id);
                    } else {
                        temp[filterKey].leagueId = [...current, id];
                    }
                    temp[filterKey].isFilter = true;
                    handleGlobalState(temp);
                }}
                onClear={() => {
                    if(currentFilter?.leagueId?.length > 0){
                        const temp = { ...globalState };
                        temp[filterKey].leagueId = [];
                        handleGlobalState(temp);
                    }
                }}
             />
             {/* <Button variant="outlined" className={styles.controlBtn}>Artist/Performer</Button> */}
              <DateRangeFilter 
                startDate={currentFilter?.startdate ? moment(currentFilter.startdate, "YYYY-MM-DD").toDate() : null}
                endDate={currentFilter?.enddate ? moment(currentFilter.enddate, "YYYY-MM-DD").toDate() : null}
                onDateChange={(start, end) => {
                    const temp = { ...globalState };
                    temp[filterKey].startdate = start ? formatDateWithHyphen(start) : null;
                    temp[filterKey].enddate = end ? formatDateWithHyphen(end) : null;
                    temp[filterKey].isFilter = true;
                    handleGlobalState(temp);
                }}
                onClear={() => {
                    const temp = { ...globalState };
                    temp[filterKey].startdate = null;
                    temp[filterKey].enddate = null;
                    temp[filterKey].startDateRange = null;
                    temp[filterKey].endDateRange = null;
                    handleGlobalState(temp);
                }}
             />
             <Button 
                variant="outlined" 
                className={styles.controlBtn}
                onClick={() => {
                    const temp = { ...globalState };
                    temp[filterKey].leagueId = [];
                    temp[filterKey].ticketStatus = [];
                    temp[filterKey].isFilter = false;
                    temp[filterKey].startdate = null;
                    temp[filterKey].enddate = null;
                    temp[filterKey].startDateRange = null;
                    temp[filterKey].endDateRange = null;
                    handleGlobalState(temp);
                }}
            >
                Clear all
            </Button>
        </div>
      </div>

      <div className={styles.tabsRow}>
           <div className={styles.tabContainer}>
                {tabs.map((label, index) => (
                    <div 
                        key={label}
                        className={`${styles.customTab} ${activeTab === index ? styles.activeTab : ''}`}
                        onClick={() => { 
                          setActiveTab(index); 
                          setPage(1); 
                          setTickets([]);
                        }}
                    >
                        {label}
                    </div>
                ))}
            </div>
      </div>
      
      {/* Paginated Table Component */}
      {(loading) ? (
        <div style={{ display: 'flex', justifyContent: 'center', padding: '2rem', height: '100%', alignItems: 'end' }}>
          <CircularProgress />
        </div>
      ) : (
        <PaginatedTable
            page={page}
            count={Math.max(1, totalPages)}
            onChange={(_, p) => setPage(p)}
        >
            {activeTab === 0 && (
              <NewListTickets
                key={page}
                tickets={sortedTickets}
                sortColumn={sortColumn}
                sortDirection={sortDirection}
                onRequestSort={handleRequestSort}
              />
            )}
            {activeTab === 1 && (
              <NewSoldTickets
                key={page}
                tickets={sortedTickets}
                sortColumn={sortColumn}
                sortDirection={sortDirection}
                onRequestSort={handleRequestSort}
              />
            )}
            {activeTab === 2 && (
              <NewDelistedTickets
                key={page}
                tickets={sortedTickets}
                sortColumn={sortColumn}
                sortDirection={sortDirection}
                onRequestSort={handleRequestSort}
              />
            )}
            {activeTab === 3 && (
              <NewUnsoldTickets
                key={page}
                tickets={sortedTickets}
                sortColumn={sortColumn}
                sortDirection={sortDirection}
                onRequestSort={handleRequestSort}
              />
            )}
        </PaginatedTable>
      )}
    </div>
  );
};

export default NewTickets;